import { type PlayerInput } from '@fafi/shared';
import { BINDING_BTN, DEFAULT_BINDINGS, type ActionBinding, type Bindings } from './bindings';

/**
 * Keyboard + mouse sampler → PlayerInput for the controlled player (§13.2). WASD move (camera-relative
 * via `cameraYaw`); action buttons come from the rebindable `bindings` (mouse LMB/RMB = pass/shoot,
 * keys for tackle/slide/through/…). Mouse-button codes are `Mouse0/1/2`. Absolute mouse aim is computed
 * by the caller from `pointerNdc()` (needs the camera) and passed in as `aimYaw`. Listens on window;
 * `pointerNdc` needs the render container's rect. Call dispose() to detach.
 */
const ACTIONS = Object.keys(BINDING_BTN) as ActionBinding[];

export class KeyboardInput {
  private readonly held = new Set<string>();
  private clientX = 0;
  private clientY = 0;

  private readonly onDown = (e: KeyboardEvent): void => {
    this.held.add(e.code);
  };
  private readonly onUp = (e: KeyboardEvent): void => {
    this.held.delete(e.code);
  };
  private readonly onMouseDown = (e: MouseEvent): void => {
    this.held.add(`Mouse${e.button}`);
  };
  private readonly onMouseUp = (e: MouseEvent): void => {
    this.held.delete(`Mouse${e.button}`);
  };
  private readonly onMouseMove = (e: MouseEvent): void => {
    this.clientX = e.clientX;
    this.clientY = e.clientY;
  };
  private readonly onContextMenu = (e: MouseEvent): void => {
    e.preventDefault(); // free RMB for "shoot"
  };
  private readonly onBlur = (): void => {
    this.held.clear(); // a lost focus must not leave keys/buttons "stuck" down
  };

  constructor(private readonly target: Window = window) {
    target.addEventListener('keydown', this.onDown);
    target.addEventListener('keyup', this.onUp);
    target.addEventListener('mousedown', this.onMouseDown);
    target.addEventListener('mouseup', this.onMouseUp);
    target.addEventListener('mousemove', this.onMouseMove);
    target.addEventListener('contextmenu', this.onContextMenu);
    target.addEventListener('blur', this.onBlur);
  }

  dispose(): void {
    this.target.removeEventListener('keydown', this.onDown);
    this.target.removeEventListener('keyup', this.onUp);
    this.target.removeEventListener('mousedown', this.onMouseDown);
    this.target.removeEventListener('mouseup', this.onMouseUp);
    this.target.removeEventListener('mousemove', this.onMouseMove);
    this.target.removeEventListener('contextmenu', this.onContextMenu);
    this.target.removeEventListener('blur', this.onBlur);
  }

  /** Is a specific key/mouse code physically held right now? (D137: the LMB goal-aim hold reads this.) */
  isHeld(code: string): boolean {
    return this.held.has(code);
  }

  /** D137: the raw held set, for the context mapper — it decides what each key MEANS this frame. */
  heldCodes(): ReadonlySet<string> {
    return this.held;
  }

  /** The pointer in normalized device coords for `rect` (x,y ∈ [−1,1], y up) — feeds the aim raycast. */
  pointerNdc(rect: { left: number; top: number; width: number; height: number }): { x: number; y: number } {
    return {
      x: rect.width > 0 ? ((this.clientX - rect.left) / rect.width) * 2 - 1 : 0,
      y: rect.height > 0 ? -(((this.clientY - rect.top) / rect.height) * 2 - 1) : 0,
    };
  }

  /**
   * Sample the current input. `cameraYaw` is the world angle the camera faces (forward). moveVec =
   * forward·(W−S) + right·(D−A), forward = (cos yaw, sin yaw), right = forward+90° = (−sin yaw, cos yaw)
   * — W follows the camera, D goes screen-right. Buttons come from `bindings` (held key/mouse codes).
   */
  sample(cameraYaw: number, aimYaw = 0, bindings: Bindings = DEFAULT_BINDINGS, aimElev = 0, buttonsOverride?: number): PlayerInput {
    let fwd = 0;
    let right = 0;
    // D127: movement reads its BINDINGS. It was hard-coded to WASD, which meant AZERTY simply could not
    // play — the arrow keys survive as fixed aliases so a broken/empty binding can never strand anyone.
    const down = (code: string, alias: string): boolean => (code !== '' && this.held.has(code)) || this.held.has(alias);
    if (down(bindings.moveUp, 'ArrowUp')) fwd += 1;
    if (down(bindings.moveDown, 'ArrowDown')) fwd -= 1;
    if (down(bindings.moveRight, 'ArrowRight')) right += 1;
    if (down(bindings.moveLeft, 'ArrowLeft')) right -= 1;

    const cos = Math.cos(cameraYaw);
    const sin = Math.sin(cameraYaw);
    let moveX = fwd * cos - right * sin;
    let moveY = fwd * sin + right * cos;
    const m = Math.hypot(moveX, moveY);
    if (m > 1) {
      moveX /= m;
      moveY /= m;
    }

    // D137: the game passes the CONTEXT MAPPER's word — the same key means different things depending on
    // what the ball is doing, and only the mapper knows that. The context-free OR below stays as the raw
    // fallback (and is what the sampler is unit-tested on): '' means UNBOUND, and without that guard an
    // unbound action matches an empty string that can never be a real KeyboardEvent.code — but IS one a
    // legacy or adversarial bindings blob can contain.
    let buttons = buttonsOverride ?? 0;
    if (buttonsOverride === undefined) {
      for (const action of ACTIONS) {
        const code = bindings[action];
        if (code !== '' && this.held.has(code)) buttons |= BINDING_BTN[action];
      }
    }

    return { moveX, moveY, aimYaw, aimElev, buttons, emote: 0 };
  }
}
